/*
 * Drop a hint — product block (snippets/product-drop-a-hint.liquid)
 *
 * Opening/closing the drawer is handled by Broadcast's <popup-component>.
 * This component only:
 *  - keeps the drawer preview in sync with the selected variant
 *  - validates the form and sends a Klaviyo client event to the recipient's profile
 *  - shows test mode / error / limit / success states
 *
 * Klaviyo event properties (used by the Klaviyo flow email template):
 * RecipientName, SenderName, Message, ProductName, ProductID, VariantID,
 * VariantTitle, CaratSize, Metal, RingSize, Price, ProductURL, ImageURL, ShopName
 * (CaratSize / Metal / RingSize are matched by option name and are '' when a product lacks that option)
 */

if (!customElements.get('drop-a-hint')) {
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/events/';
  const KLAVIYO_REVISION = '2026-07-15';
  const STORAGE_KEY = 'dropAHintLog';
  const MIN_FILL_MS = 2500;

  customElements.define(
    'drop-a-hint',
    class DropAHint extends HTMLElement {
      connectedCallback() {
        this.data = this.parseData();
        if (!this.data) return;

        this.form = this.querySelector('[data-drop-hint-form]');
        this.submitButton = this.form?.querySelector('[type="submit"]');
        this.status = this.querySelector('[data-drop-hint-status]');
        this.payload = this.querySelector('[data-drop-hint-payload]');
        this.payloadCode = this.querySelector('[data-drop-hint-payload-code]');
        this.successMessage = this.querySelector('[data-drop-hint-success]');
        this.openButton = this.querySelector('[data-popup-open]');
        this.closeButton = this.querySelector('[data-popup-close]');
        this.image = this.querySelector('[data-drop-hint-image]');
        this.options = this.querySelector('[data-drop-hint-options]');
        this.previewHeading = this.querySelector('[data-drop-hint-heading]');
        this.previewSender = this.querySelector('[data-drop-hint-sender]');
        this.previewNote = this.querySelector('[data-drop-hint-note]');
        this.previewMessage = this.querySelector('[data-drop-hint-message]');
        this.previewNoteSender = this.querySelector('[data-drop-hint-note-sender]');

        if (!this.form || !this.submitButton) return;

        this.variantId = String(this.data.currentVariantId);
        this.openedAt = Date.now();
        this.controller = new AbortController();
        const {signal} = this.controller;

        this.form.addEventListener('submit', (event) => this.onSubmit(event), {signal});
        this.form.addEventListener('input', () => this.renderPreview(), {signal});
        this.openButton?.addEventListener('click', () => this.onOpen(), {signal});
        // Start fresh every time: clear the form whenever the drawer closes (✕, Escape, outside click or after sending)
        this.querySelector('dialog')?.addEventListener('close', () => this.resetForm(), {signal});

        if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
          this.unsubscribeVariantChange = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, (event) =>
            this.onVariantChange(event)
          );
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.unsubscribeVariantChange?.();
      }

      parseData() {
        try {
          return JSON.parse(this.querySelector('[data-drop-hint-data]')?.textContent || 'null');
        } catch (error) {
          console.warn('drop-a-hint: invalid product data', error);
          return null;
        }
      }

      get variant() {
        return this.data.variants[this.variantId] || null;
      }

      onOpen() {
        this.openedAt = Date.now();
        this.successMessage.textContent = '';
      }

      onVariantChange({data} = {}) {
        const variantId = data?.variant?.id;
        if (!variantId || data.sectionId !== this.dataset.sectionId) return;
        if (!this.data.variants[variantId]) return;

        this.variantId = String(variantId);
        this.renderVariant();
      }

      renderVariant() {
        const variant = this.variant;
        if (!variant) return;

        if (this.image && variant.src) {
          this.image.srcset = variant.srcset;
          this.image.src = variant.src;
          this.image.alt = variant.alt;
        }

        if (this.options) {
          // Metal / carat / size row; products without those options fall back to the variant title
          const size = variant.ringSize ? this.dataset.sizeTemplate.replace('[size]', variant.ringSize) : '';
          let values = [variant.metal, variant.caratSize, size].filter(Boolean);
          if (!values.length && variant.title) values = [variant.title];

          this.options.replaceChildren(
            ...values.map((value) => {
              const item = document.createElement('li');
              item.textContent = value;
              return item;
            })
          );
          this.options.hidden = !values.length;
        }
      }

      /* Mirrors the email: "Psst Alex..." / "Sam found a special piece..." / "note" — Sam */
      renderPreview() {
        const value = (name) => String(this.form.elements[name]?.value || '').trim();
        const recipient = value('recipient_name');
        const sender = value('sender_name');
        const message = value('message');
        const heading = this.dataset.previewHeading;

        if (this.previewHeading) this.previewHeading.textContent = recipient ? `${heading} ${recipient}...` : `${heading}...`;
        if (this.previewSender) this.previewSender.textContent = sender || this.dataset.someoneText;

        if (this.previewNote) {
          this.previewMessage.textContent = message;
          this.previewNoteSender.textContent = sender || this.dataset.someoneText;
          this.previewNote.hidden = !message;
        }
      }

      /* Bots fill the honeypot or submit instantly */
      isLikelyBot(formData) {
        return Boolean(formData.get('drop_hint_check')) || Date.now() - this.openedAt < MIN_FILL_MS;
      }

      readLog() {
        const today = new Date().toISOString().slice(0, 10);
        try {
          const log = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
          return log.date === today ? log : {date: today, count: 0};
        } catch (error) {
          return {date: today, count: 0};
        }
      }

      writeLog(log) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
        } catch (error) {
          // Storage unavailable (e.g. private mode): carry on without the daily cap
        }
      }

      buildPayload(formData) {
        const value = (key) => String(formData.get(key) || '').trim();
        const variant = this.variant || {};

        const payload = {
          data: {
            type: 'event',
            attributes: {
              properties: {
                RecipientName: value('recipient_name'),
                SenderName: value('sender_name'),
                Message: value('message'),
                ProductName: this.data.title,
                ProductID: this.data.productId,
                VariantID: Number(this.variantId),
                VariantTitle: variant.title || '',
                CaratSize: variant.caratSize || '',
                Metal: variant.metal || '',
                RingSize: variant.ringSize || '',
                Price: variant.price || '',
                ProductURL: `${this.data.url}?variant=${this.variantId}`,
                ImageURL: variant.emailImage || '',
                ShopName: this.data.shop,
              },
              metric: {data: {type: 'metric', attributes: {name: this.dataset.eventName}}},
              // Only the email: never overwrite the recipient's profile with a name typed by someone else
              profile: {data: {type: 'profile', attributes: {email: value('recipient_email')}}},
            },
          },
        };

        if (window.crypto?.randomUUID) payload.data.attributes.unique_id = window.crypto.randomUUID();

        return payload;
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-busy') === 'true') return;

        this.setStatus('');
        const formData = new FormData(this.form);

        if (this.isLikelyBot(formData)) {
          this.onSuccess();
          return;
        }

        const log = this.readLog();
        if (log.count >= Number(this.dataset.dailyLimit || 5)) {
          this.setStatus(this.dataset.limitText);
          return;
        }

        const payload = this.buildPayload(formData);

        if (this.hasAttribute('data-test-mode')) {
          this.payloadCode.textContent = JSON.stringify(payload, null, 2);
          this.payload.hidden = false;
          return;
        }

        this.submitButton.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.siteKey)}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', revision: KLAVIYO_REVISION},
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            // Include Klaviyo's reason (e.g. 403 permission_denied = domain not on the public key allowlist)
            const detail = await response.text().catch(() => '');
            throw new Error(`Klaviyo responded ${response.status} ${detail.slice(0, 300)}`);
          }

          log.count += 1;
          this.writeLog(log);
          this.onSuccess();
        } catch (error) {
          console.warn('drop-a-hint:', error);
          this.setStatus(this.dataset.errorText);
        } finally {
          this.submitButton.removeAttribute('aria-busy');
        }
      }

      onSuccess() {
        this.resetForm();
        // Close through the theme's own close button so its animation, scroll unlock and focus return run
        this.closeButton?.click();
        this.successMessage.textContent = this.dataset.successText;
      }

      resetForm() {
        this.form.reset();
        this.payload.hidden = true;
        this.setStatus('');
        this.renderPreview();
      }

      setStatus(message) {
        this.status.textContent = message || '';
        this.status.hidden = !message;
      }
    }
  );
}
